'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { HiOutlineSearch } from '@react-icons/all-files/hi/HiOutlineSearch';
import { HiOutlineDocument } from '@react-icons/all-files/hi/HiOutlineDocument';
import { HiOutlineCheckCircle } from '@react-icons/all-files/hi/HiOutlineCheckCircle';
import { HiOutlineExclamationCircle } from '@react-icons/all-files/hi/HiOutlineExclamationCircle';
import {
  StyledToolHero,
  StyledToolBody,
  StyledCalculator,
  StyledFieldsGrid,
  StyledFormGroup,
  StyledCalculateButton,
  StyledResult,
  StyledCheckList,
  StyledCheckItem,
  StyledWidgetsSection,
  StyledWidgetGrid,
  StyledWidgetCard,
  StyledRelatedTools,
  StyledRelatedToolGrid,
  StyledRelatedToolCard,
  StyledCtaBanner,
} from './CanonicalUrlCheckerTool.styles';

interface IWidget {
  _id: string;
  name: string;
  slug: string;
  logo: string;
  teaser: string;
}

interface IRelatedTool {
  slug: string;
  name: string;
  description: string;
}

interface ICheck {
  label: string;
  pass: boolean;
  detail: string;
}

interface IToolResult {
  checks: ICheck[];
  issueCount: number;
  status: 'poor' | 'fair' | 'good';
  pageUrl: string;
  canonicalUrl: string;
}

interface IProps {
  recommendedWidgets: IWidget[];
  relatedTools: IRelatedTool[];
}

function normalizeUrl(url: string): URL | null {
  try {
    return new URL(url.trim());
  } catch {
    return null;
  }
}

function checkCanonical(pageUrl: string, canonicalUrl: string): IToolResult {
  const checks: ICheck[] = [];

  const page = normalizeUrl(pageUrl);
  const canon = normalizeUrl(canonicalUrl);

  if (!page) {
    return {
      checks: [{ label: 'Valid page URL', pass: false, detail: 'The page URL is not a valid absolute URL. It must start with http:// or https://.' }],
      issueCount: 1,
      status: 'poor',
      pageUrl,
      canonicalUrl,
    };
  }

  if (!canon) {
    return {
      checks: [{ label: 'Valid canonical URL', pass: false, detail: 'The canonical URL is not a valid absolute URL. It must start with http:// or https://.' }],
      issueCount: 1,
      status: 'poor',
      pageUrl,
      canonicalUrl,
    };
  }

  // Protocol check
  const protocolMatch = page.protocol === canon.protocol;
  checks.push({
    label: 'Protocol match (http vs https)',
    pass: protocolMatch,
    detail: protocolMatch
      ? `Both URLs use ${canon.protocol}. Good.`
      : `Page uses ${page.protocol} but canonical uses ${canon.protocol}. This is a mismatch that splits link equity between two protocol versions.`,
  });

  // www vs non-www
  const pageHost = page.hostname;
  const canonHost = canon.hostname;
  const wwwMatch = pageHost === canonHost;
  checks.push({
    label: 'www vs non-www consistency',
    pass: wwwMatch,
    detail: wwwMatch
      ? `Both URLs use the same host: ${canonHost}. Good.`
      : `Page host is "${pageHost}" but canonical host is "${canonHost}". Set a consistent www preference and redirect the other version.`,
  });

  // Trailing slash
  const pageSlash = page.pathname.endsWith('/');
  const canonSlash = canon.pathname.endsWith('/');
  const slashMatch = pageSlash === canonSlash || page.pathname === canon.pathname;
  checks.push({
    label: 'Trailing slash consistency',
    pass: slashMatch,
    detail: slashMatch
      ? 'Trailing slash usage is consistent between both URLs. Good.'
      : `Page path "${page.pathname}" and canonical path "${canon.pathname}" differ on trailing slashes. Choose one format and stick with it across all URLs.`,
  });

  // Path case
  const pathCaseMatch = page.pathname === canon.pathname;
  checks.push({
    label: 'URL path case match',
    pass: pathCaseMatch,
    detail: pathCaseMatch
      ? 'Path cases match exactly. Good.'
      : `Page path "${page.pathname}" differs from canonical path "${canon.pathname}" in casing. Lowercase URLs are strongly recommended and must match exactly.`,
  });

  // Query parameters
  const canonHasQuery = canon.search.length > 0;
  checks.push({
    label: 'No query parameters in canonical',
    pass: !canonHasQuery,
    detail: canonHasQuery
      ? `Canonical URL contains query parameters: ${canon.search}. Canonical tags should generally point to clean, parameter-free URLs unless parameters define unique content.`
      : 'Canonical URL has no query parameters. Good.',
  });

  // Exact match
  const exactMatch = page.href === canon.href;
  checks.push({
    label: 'Exact URL match (self-referencing canonical)',
    pass: exactMatch,
    detail: exactMatch
      ? 'The page URL and canonical URL are identical. This is a self-referencing canonical, which is the best practice for pages you want indexed.'
      : 'The page URL and canonical URL are not identical. If this is intentional (consolidating duplicate content), verify all other checks pass. If not intentional, update the canonical tag to exactly match the page URL.',
  });

  const issueCount = checks.filter((c) => !c.pass).length;
  const status: 'poor' | 'fair' | 'good' =
    issueCount === 0 ? 'good' : issueCount <= 2 ? 'fair' : 'poor';

  return { checks, issueCount, status, pageUrl: page.href, canonicalUrl: canon.href };
}

function getWidgetDescription(slug: string): string {
  switch (slug) {
    case 'accordion':
      return 'Canonical issues often stem from duplicate content. Use accordions to consolidate information on fewer, stronger pages rather than spreading thin content across many similar URLs.';
    case 'tabs':
      return 'Tabs help you present multiple content sections on a single canonical URL, reducing the need for similar pages that create canonicalization headaches.';
    case 'comparison-tables':
      return 'Comparison tables let you consolidate product or feature comparisons on one canonical page rather than creating similar pages that compete with each other.';
    case 'feeds':
      return 'Content feeds help you surface related content on canonical pages, keeping users on the correct version and reducing crawl of duplicate or thin pages.';
    default:
      return 'Strengthen your canonical pages with content that keeps users engaged and signals quality to search engines.';
  }
}

function getWidgetCTA(slug: string): string {
  switch (slug) {
    case 'accordion': return 'Add Accordion Free →';
    case 'tabs': return 'Add Tabs Free →';
    case 'comparison-tables': return 'Add Tables Free →';
    case 'feeds': return 'Add Feeds Free →';
    default: return 'Try It Free →';
  }
}

const TOOL_ICONS: Record<string, React.ReactNode> = {
  'keyword-density-checker': <HiOutlineSearch />,
  'xml-sitemap-validator': <HiOutlineDocument />,
  'anchor-text-analyzer': <HiOutlineSearch />,
};

export const CanonicalUrlCheckerTool = ({ recommendedWidgets, relatedTools }: IProps) => {
  const [pageUrl, setPageUrl] = useState('');
  const [canonicalUrl, setCanonicalUrl] = useState('');
  const [result, setResult] = useState<IToolResult | null>(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const resultRef = useRef<HTMLDivElement>(null);

  const handleCheck = () => {
    if (!pageUrl.trim()) {
      setError('Please enter the page URL.');
      return;
    }
    if (!canonicalUrl.trim()) {
      setError('Please enter the canonical URL.');
      return;
    }
    setError('');

    const res = checkCanonical(pageUrl.trim(), canonicalUrl.trim());
    setResult(res);
    setHasCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const statusLabel =
    result?.status === 'good' ? 'Canonical Match' : result?.status === 'fair' ? 'Minor Issues' : 'Mismatch';

  return (
    <>
      <StyledToolHero>
        <h1>Free <span className="highlight">Canonical URL Checker</span></h1>
        <p>
          Compare your page URL and canonical URL to detect mismatches,
          trailing slash conflicts, and protocol issues. Free, no sign-up required.
        </p>
      </StyledToolHero>

      <StyledToolBody>
        <StyledCalculator>
          <h2>Check Your Canonical URL</h2>

          <StyledFieldsGrid>
            <StyledFormGroup>
              <label htmlFor="pageUrl">Page URL</label>
              <input
                id="pageUrl"
                type="text"
                placeholder="https://www.example.com/blog/my-post"
                value={pageUrl}
                onChange={(e) => setPageUrl(e.target.value)}
              />
              <p className="hint">The actual URL of the page as it appears in the browser address bar</p>
            </StyledFormGroup>

            <StyledFormGroup>
              <label htmlFor="canonicalUrl">Canonical URL</label>
              <input
                id="canonicalUrl"
                type="text"
                placeholder="https://www.example.com/blog/my-post"
                value={canonicalUrl}
                onChange={(e) => setCanonicalUrl(e.target.value)}
              />
              <p className="hint">The URL in your page&apos;s {'<link rel="canonical" href="...">'} tag</p>
            </StyledFormGroup>
          </StyledFieldsGrid>

          {error && (
            <p style={{ color: '#e22029', fontSize: '0.875rem', marginBottom: '16px' }}>{error}</p>
          )}

          <StyledCalculateButton onClick={handleCheck}>
            Check Canonical URL
          </StyledCalculateButton>
        </StyledCalculator>

        {hasCalculated && result && (
          <>
            <StyledResult status={result.status} ref={resultRef}>
              <p className="result-label">Canonicalization Result</p>
              <p className="result-number">{statusLabel}</p>
              <span className="result-badge">
                {result.issueCount === 0 ? 'All checks passed' : `${result.issueCount} issue${result.issueCount !== 1 ? 's' : ''} found`}
              </span>

              <StyledCheckList>
                {result.checks.map((check, i) => (
                  <StyledCheckItem key={i} pass={check.pass}>
                    <span className="check-icon">
                      {check.pass ? <HiOutlineCheckCircle /> : <HiOutlineExclamationCircle />}
                    </span>
                    <div className="check-body">
                      <p className="check-label">{check.label}</p>
                      <p className="check-detail">{check.detail}</p>
                    </div>
                  </StyledCheckItem>
                ))}
              </StyledCheckList>

              {result.status === 'good' && (
                <div className="result-pain">
                  <strong>All checks passed:</strong> Your canonical URL is correctly configured.
                  Make sure your canonical tag is present in the page HTML head section and that
                  it is not accidentally blocked by JavaScript or conditional rendering.
                </div>
              )}

              {result.status === 'fair' && (
                <div className="result-pain">
                  <strong>Minor issues detected:</strong> Small canonicalization differences can
                  cause Google to choose the wrong version or split ranking signals between
                  URLs. Fix the flagged issues to ensure consistent canonicalization across your site.
                </div>
              )}

              {result.status === 'poor' && (
                <div className="result-pain">
                  <strong>Significant mismatch:</strong> Multiple canonicalization conflicts found.
                  This configuration sends conflicting signals to search engines and can lead to the
                  wrong page version being indexed. Fix all flagged issues and verify with Google
                  Search Console after updating.
                </div>
              )}
            </StyledResult>

            <StyledWidgetsSection>
              <h2>Build Stronger Canonical Pages</h2>
              <p className="subtitle">
                These widgets help you consolidate content onto fewer, higher-quality canonical pages.
              </p>
              <StyledWidgetGrid>
                {recommendedWidgets.map((widget) => (
                  <StyledWidgetCard
                    key={widget._id}
                    href={`/widgets/${widget.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="widget-header">
                      {widget.logo && (
                        <Image
                          src={widget.logo}
                          alt={`${widget.name} logo`}
                          width={48}
                          height={48}
                        />
                      )}
                      <h3>{widget.name}</h3>
                    </div>
                    <p>{getWidgetDescription(widget.slug)}</p>
                    <span className="widget-cta">{getWidgetCTA(widget.slug)}</span>
                  </StyledWidgetCard>
                ))}
              </StyledWidgetGrid>
            </StyledWidgetsSection>
          </>
        )}

        {relatedTools.length > 0 && (
          <StyledRelatedTools>
            <h2>Explore More Free SEO Tools</h2>
            <p className="subtitle">Other free tools to audit and improve your technical SEO.</p>
            <StyledRelatedToolGrid>
              {relatedTools.map((tool) => (
                <StyledRelatedToolCard
                  key={tool.slug}
                  as={Link}
                  href={`/free-tools/seo/${tool.slug}`}
                >
                  <span className="tool-icon">{TOOL_ICONS[tool.slug] || <HiOutlineSearch />}</span>
                  <h3>{tool.name}</h3>
                  <p>{tool.description}</p>
                  <span className="tool-cta">Use Tool →</span>
                </StyledRelatedToolCard>
              ))}
            </StyledRelatedToolGrid>
          </StyledRelatedTools>
        )}

      </StyledToolBody>
    </>
  );
};
